import { computed, inject } from '@angular/core';
import { type TimeseriesDto } from '@api/devices';
import { type DeviceChart, type DeviceData, DeviceService } from '@devices';
import { type LocationData, LocationService, type Space } from '@locations';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { type AvsDateRangeIds } from '@ui';

interface DashboardState {
  charts: Record<string, DeviceChart | null>;
  devices: Record<string, DeviceData | null>;
  locations: Record<string, LocationData | null>;
  spaceLists: Record<string, Space[] | null>;
  spaces: Record<string, Space | null>;
}

export function getChartKey(deviceId: string, point: string, dateRange?: AvsDateRangeIds) {
  return `${deviceId}:${point}:${dateRange ?? ''}`;
}

export const DashboardStore = signalStore(
  { providedIn: 'root' },
  withState<DashboardState>({
    charts: {},
    devices: {},
    locations: {},
    spaceLists: {},
    spaces: {},
  }),
  withMethods((
    store,
    deviceService = inject(DeviceService),
    locationService = inject(LocationService),
  ) => ({
    chartData(key: string) {
      return computed(() => store.charts()[key]);
    },

    deviceDetails(deviceId: string) {
      return computed(() => store.devices()[deviceId]);
    },

    invalidateAll() {
      patchState(store, {
        charts: {},
        devices: {},
        locations: {},
        spaceLists: {},
        spaces: {},
      });
    },

    /**
     * Register a chart data request. Returns a stable key for use with chartData().
     * Identical requests are deduplicated — only one HTTP call is made.
     */
    loadChartData(deviceId: string, payload: TimeseriesDto, dateRange?: AvsDateRangeIds) {
      const key = getChartKey(deviceId, payload.point, dateRange);

      if (key in store.charts()) {
        return key;
      }

      patchState(store, { charts: { ...store.charts(), [key]: null } });

      deviceService.getChartsData({ data: payload, deviceId }).subscribe({
        next: (data) => patchState(store, { charts: { ...store.charts(), [key]: data } }),
      });

      return key;
    },

    /**
     * Register a device details request. Returns the deviceId as key.
     * Identical requests are deduplicated.
     */
    loadDeviceDetails(deviceId: string) {
      if (deviceId in store.devices()) {
        return deviceId;
      }

      patchState(store, { devices: { ...store.devices(), [deviceId]: null } });

      deviceService.getDeviceDetailsByDeviceId(deviceId).subscribe({
        next: (data) => patchState(store, { devices: { ...store.devices(), [deviceId]: data } }),
      });

      return deviceId;
    },

    /**
     * Register a location info request. Returns the locationId as key.
     * Identical requests are deduplicated.
     */
    loadLocationInfo(locationId: string) {
      if (locationId in store.locations()) {
        return locationId;
      }

      patchState(store, { locations: { ...store.locations(), [locationId]: null } });

      locationService.getLocationInfoById(locationId).subscribe({
        next: (data) => patchState(store, { locations: { ...store.locations(), [locationId]: data } }),
      });

      return locationId;
    },

    /**
     * Register a space info request. Returns a composite key.
     * Identical requests are deduplicated.
     */
    loadSpaceInfo(locationId: string, spaceId: string) {
      const key = `${locationId}:${spaceId}`;

      if (key in store.spaces()) {
        return key;
      }

      patchState(store, { spaces: { ...store.spaces(), [key]: null } });

      locationService.getSpaceInfoById(locationId, spaceId).subscribe({
        next: (data) => patchState(store, { spaces: { ...store.spaces(), [key]: data } }),
      });

      return key;
    },

    /**
     * Register a spaces list request. Returns the locationId as key.
     * Identical requests are deduplicated.
     */
    loadSpacesList(locationId: string) {
      if (locationId in store.spaceLists()) {
        return locationId;
      }

      patchState(store, { spaceLists: { ...store.spaceLists(), [locationId]: null } });

      locationService.getSpacesList(locationId).subscribe({
        next: (data) => patchState(store, { spaceLists: { ...store.spaceLists(), [locationId]: data } }),
      });

      return locationId;
    },

    locationInfo(locationId: string) {
      return computed(() => store.locations()[locationId]);
    },

    spaceInfo(key: string) {
      return computed(() => store.spaces()[key]);
    },

    spacesList(locationId: string) {
      return computed(() => store.spaceLists()[locationId]);
    },
  })),
);
